import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ParkingBookingStatus} from '../../../../pages/client/poi/parking-booking/parking-booking.component';
import {NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import {ParkingService} from '../parking.service';
import {NotificationService} from '../../../notification/notification.service';
import {SubscriptionUtils} from '../../../commons/subscription.utils';
import {takeUntil} from 'rxjs/operators';
import {VehicleDriverInfo} from '../model/vehicle-driver.info';
import {fifteenMinuteInterval, validateTillLaterThanFrom} from '../../../validators';
import {TravisParkingHours, TravisParkingInfo} from './model/travis-parking.info';
import {TravisBookingControls, TravisBookingInfo, TravisParkingAvailability} from './model/travis-booking.info';
import {NewBookingInfo} from '../bosch/model/bosch-booking.info';
import {TravisAvailabilityRequest} from './model/travis-availability.request';
import {TravisBookingForm} from './model/travis-booking.form';

let moment = require('moment');

@Component({
  selector: 'app-travis-parking-form',
  templateUrl: './travis-parking-form.component.html',
  styleUrls: ['./travis-parking-form.component.scss']
})
export class TravisParkingFormComponent implements OnInit, OnDestroy {

  @Input()
  public modalReference: any;

  @Input()
  public vehicleId: number;

  @Input()
  public parkingId: string;

  @Input()
  public bookingId: number;

  @Input()
  public taskId: number;

  @Input()
  public etaToParking: string;

  @Output()
  public newBooking = new EventEmitter<NewBookingInfo>();

  @Output()
  public bookingStatus = new EventEmitter<ParkingBookingStatus>();

  public minDate: NgbDateStruct;
  public parkingAvailable = false;
  public isFreezer = false;
  public freezersAllowed = true;
  public previousStart: string;
  public previousEnd: string;
  public parkingInfo: TravisParkingInfo;
  public parkingAvailability: TravisParkingAvailability;
  public bookingInfo: TravisBookingInfo;
  public bookingForm: FormGroup<TravisBookingControls>;
  public hasCredentials = true;
  public defaultHours = [9, 10, 11, 12, 24];

  private vehicleDriver: VehicleDriverInfo;
  private subSubject = new Subject<boolean>();

  constructor(private fb: FormBuilder,
              private parkingService: ParkingService,
              private notification: NotificationService) {
  }

  public ngOnInit(): void {
    this.setMinDate();
    if (this.bookingId != null) {
      this.parkingService.fetchTravisBookingInfo(this.bookingId).pipe(takeUntil(this.subSubject)).subscribe({
        next: (data) => {
          if (data != null) {
            this.bookingInfo = data;
            this.bookingStatus.emit(data.status);
          } else {
            this.hasCredentials = false;
          }
        },
        error: () => this.notification.loadingError()
      });
    } else {
      this.parkingService.fetchVehicleDriver(this.vehicleId).pipe(takeUntil(this.subSubject)).subscribe({
        next: (data) => {
          this.isFreezer = data.freezer;
          this.vehicleDriver = data;
          this.buildForm(data);
        },
        error: () => this.buildForm(null)
      })
      this.parkingService.fetchTravisParkingInfo(this.parkingId).pipe(takeUntil(this.subSubject)).subscribe({
        next: (data) => {
          if (data != null) {
            data.parking.prices.sort((a, b) => a.duration > b.duration ? 1 : -1)
            this.parkingInfo = data;
            this.freezersAllowed = data.parking.options.coolingMotorsAllowed;
          } else {
            this.hasCredentials = false;
          }
        },
        error: () => this.notification.loadingError()
      });
    }
  }

  public closeModal(): void {
    this.modalReference.close();
    this.modalReference = null;
  }

  private buildForm(data: VehicleDriverInfo): void {
    if (this.etaToParking != null) {
      let eta = moment(this.etaToParking, 'YYYY-MM-DD HH:mm');
      let minutes = eta.minutes();
      this.etaToParking = eta.add(minutes < 30 ? 30 - minutes : 60 - minutes, 'minutes').format('YYYY-MM-DD HH:mm');
    }
    let startDate = this.etaToParking != null ? this.etaToParking : '';
    this.bookingForm = this.fb.group<TravisBookingControls>({
      startDate: new FormControl(startDate, Validators.compose([Validators.required, fifteenMinuteInterval])),
      endDate: new FormControl('', Validators.compose([Validators.required, fifteenMinuteInterval])),
      driverName: new FormControl(data != null ? this.getDriverName(data) : '', Validators.required),
      licensePlate: new FormControl(data != null ? data.licensePlate : null, Validators.required),
      trailerLicensePlate: new FormControl(data != null ? data.trailerLicensePlate : null)
    }, {
      validators: validateTillLaterThanFrom('startDate', 'endDate')
    })
  }

  private getDriverName(data: VehicleDriverInfo): string {
    return data.lastName != ''
      ? data.firstName + ' ' + data.lastName
      : data.firstName
  }

  public checkAvailability(): void {
    let startDate = this.bookingForm.value.startDate;
    let endDate = this.bookingForm.value.endDate;
    if (startDate?.length > 0 && endDate?.length > 0) {
      let request = new TravisAvailabilityRequest(this.parkingId, startDate, endDate, this.parkingInfo.timezone);
      this.parkingService.fetchTravisParkingAvailability(request).pipe(takeUntil(this.subSubject))
        .subscribe({
          next: (data) => this.handlePricing(data, startDate, endDate),
          error: () => this.notification.loadingError()
        })
    }
  }

  public datePicked(field: string): void {
    let control = this.bookingForm.get(field);
    control?.markAsTouched({ onlySelf: true });
  }

  private handlePricing(data: TravisParkingAvailability, startDate: string, endDate: string): void {
    if (data != null) {
      this.parkingAvailability = data;
      this.parkingAvailable = data.available;
      this.previousStart = startDate;
      this.previousEnd = endDate;
    } else {
      this.hasCredentials = false;
    }
  }

  public bookParking(): void {
    let form = this.toBookingForm();
    this.parkingService.bookTravisParking(form).pipe(takeUntil(this.subSubject)).subscribe({
      next: (data) => {
        if (data != null) {
          this.notification.savingSuccess();
          this.bookingInfo = data;
          this.bookingId = data.id;
          this.newBooking.emit({
            bookingId: data.id,
            driverLang: this.vehicleDriver.driverLang,
            reservationLink: data.instructionsLink,
            startDate: form.startDate
          });
        } else {
          this.hasCredentials = false;
        }
      },
      error: () => this.notification.storingError()
    });
  }

  private toBookingForm(): TravisBookingForm {
    return new TravisBookingForm(
      this.parkingId,
      this.bookingForm.value.startDate,
      this.bookingForm.value.endDate,
      this.bookingForm.value.driverName,
      this.vehicleDriver.driverLang,
      this.parkingInfo.timezone,
      this.taskId,
      this.vehicleId,
      this.bookingForm.value.licensePlate,
      this.bookingForm.value.trailerLicensePlate
    );
  }

  public cancelBooking(): void {
    this.parkingService.cancelTravisBooking(this.bookingId).pipe(takeUntil(this.subSubject)).subscribe({
      next: (success) => {
        if (success == null) {
          this.hasCredentials = false;
        } else if (success) {
          this.bookingInfo.status = 'CANCELLED';
          this.newBooking.emit(null);
          this.closeModal();
          this.notification.deletingSuccess();
        } else {
          this.notification.deletingError();
        }
      },
      error: () => this.notification.deletingError()
    });
  }

  public setEnd(hour: number): void {
    let startDate = this.bookingForm.value.startDate;
    if (startDate?.length > 0) {
      let endDate = moment(startDate, 'YYYY-MM-DD HH:mm').add(hour, 'hours').format('YYYY-MM-DD HH:mm');
      this.bookingForm.patchValue({
        endDate: endDate
      });
    }
  }

  public isOpen247(entry: TravisParkingHours): boolean {
    return entry.open24h
      || (entry.monday.length === 1 && entry.monday[0].from === entry.monday[0].to
          && entry.tuesday.length === 1 && entry.tuesday[0].from === entry.tuesday[0].to
          && entry.wednesday.length === 1 && entry.wednesday[0].from === entry.wednesday[0].to
          && entry.thursday.length === 1 && entry.thursday[0].from === entry.thursday[0].to
          && entry.friday.length === 1 && entry.friday[0].from === entry.friday[0].to
          && entry.saturday.length === 1 && entry.saturday[0].from === entry.saturday[0].to
          && entry.sunday.length === 1 && entry.sunday[0].from === entry.sunday[0].to
      );
  }

  private setMinDate(): void {
    let current = new Date();
    this.minDate = {
      year: current.getUTCFullYear(),
      month: current.getUTCMonth() + 1,
      day: current.getUTCDate()
    };
  }

  public ngOnDestroy(): void {
    SubscriptionUtils.safeUnsubscribeSubject(this.subSubject);
  }
}
